import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'

/**
 * Multi-step flow managed via React Context.
 * - Any child can navigate with `useFlow()` -> { goTo, next, prev, discard }
 * - Steps are declared with <Flow.Step id="..."> and the flow renders the active one.
 * - Discard terminates the flow (calls onDiscard) from anywhere.
 */

// ---------------------------
// Types
// ---------------------------
export type StepId = string

export interface FlowContextValue {
  /** Current step id */
  step: StepId
  /** Ordered list of step ids */
  steps: StepId[]
  /** Index of current step in `steps` */
  index: number
  /** Navigate to a specific step id (must exist) */
  goTo: (id: StepId) => void
  /** Advance to next step, if any */
  next: () => void
  /** Go to previous step, if any */
  prev: () => void
  /** Abort the flow entirely */
  discard: (reason?: string) => void
  /** Whether the flow is discarded */
  discarded: boolean
}

const FlowContext = createContext<FlowContextValue | null>(null)

export function useFlow(): FlowContextValue {
  const ctx = useContext(FlowContext)
  if (!ctx) throw new Error('useFlow must be used inside <Flow.Provider>')
  return ctx
}

// ---------------------------
// Flow Provider
// ---------------------------
export interface FlowProviderProps {
  /** Initial step id */
  initial: StepId
  /** Ordered steps. Also inferred from <Flow.Step> children if not provided. */
  steps?: StepId[]
  /** Called when user discards the flow */
  onDiscard?: (reason?: string) => void
  /** Optional callback when finishing on last step -> next() */
  onComplete?: () => void
  children: React.ReactNode
}

export function FlowProvider({
  initial,
  steps: stepsProp,
  onDiscard,
  onComplete,
  children
}: FlowProviderProps) {
  // Steps can be controlled or inferred (from children passed to <Flow.Root>)
  const inferredStepsRef = useRef<StepId[] | null>(null)

  const [step, setStep] = useState<StepId>(initial)
  const [discarded, setDiscarded] = useState(false)

  const steps = useMemo<StepId[]>(() => {
    // prefer explicit steps
    return stepsProp ?? inferredStepsRef.current ?? [initial]
  }, [stepsProp, initial])

  const index = useMemo(
    () =>
      Math.max(
        0,
        steps.findIndex(s => s === step)
      ),
    [steps, step]
  )

  const goTo = useCallback(
    (id: StepId) => {
      if (!steps.includes(id)) {
        throw new Error(
          `Flow: unknown step id '${id}'. Known: ${steps.join(', ')}`
        )
      }
      setStep(id)
    },
    [steps]
  )

  const next = useCallback(() => {
    const i = steps.findIndex(s => s === step)
    const hasNext = i >= 0 && i < steps.length - 1
    if (hasNext) setStep(steps[i + 1])
    else onComplete?.()
  }, [step, steps, onComplete])

  const prev = useCallback(() => {
    const i = steps.findIndex(s => s === step)
    const hasPrev = i > 0
    if (hasPrev) setStep(steps[i - 1])
  }, [step, steps])

  const discard = useCallback(
    (reason?: string) => {
      setDiscarded(true)
      setStep(initial)
      onDiscard?.(reason)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onDiscard]
  )

  const value = useMemo<FlowContextValue>(
    () => ({ step, steps, index, goTo, next, prev, discard, discarded }),
    [step, steps, index, goTo, next, prev, discard, discarded]
  )

  return (
    <FlowContext.Provider value={value}>
      {/* Attach an internal registrar so <Flow.Root> can infer steps order from children */}
      <FlowRegistrar setInferred={ids => (inferredStepsRef.current = ids)}>
        {children}
      </FlowRegistrar>
    </FlowContext.Provider>
  )
}

// ---------------------------
// Flow Root & Steps
// ---------------------------
interface RootProps {
  /** Optional explicit order of steps; overrides child inference */
  order?: StepId[]
  className?: string
  children: React.ReactNode
}

/**
 * Wraps <Flow.Step> children and renders the currently active one.
 * If `order` is provided, it defines the step sequence; otherwise inferred from children order.
 */
export function Root({ order, className, children }: RootProps) {
  const { step } = useFlow()

  const items = React.Children.toArray(children).filter(
    Boolean
  ) as React.ReactElement<StepProps>[]

  const byId = new Map<StepId, React.ReactElement<StepProps>>()
  items.forEach(el => {
    if (el.type === Step && el.props?.id) byId.set(el.props.id, el)
  })

  const sequence =
    order ??
    items
      .filter(
        (el): el is React.ReactElement<StepProps> => (el as any).type === Step
      )
      .map(el => el.props.id)

  // Render only active step
  const active =
    byId.get(step) ?? (sequence.length ? byId.get(sequence[0]) : null)

  return <div className={className}>{active ?? null}</div>
}

interface StepProps {
  id: StepId
  children: React.ReactNode
}

export function Step({ id: _id, children }: StepProps) {
  // Step is just a marker; rendering is handled by <Flow.Root>
  return <>{children}</>
}

// ---------------------------
// Internal: Registrar to infer step order from children
// ---------------------------
function FlowRegistrar({
  children,
  setInferred
}: {
  children: React.ReactNode
  setInferred: (ids: StepId[]) => void
}) {
  const childArray = React.Children.toArray(children) as React.ReactElement[]

  // Search for a <Flow.Root> and read its children to infer steps
  const roots = childArray.filter(
    el => (el as any).type === Root
  ) as React.ReactElement<RootProps>[]
  const firstRoot = roots[0]

  React.useEffect(() => {
    if (!firstRoot) return
    const inner = React.Children.toArray(
      firstRoot.props.children
    ) as React.ReactElement<StepProps>[]
    const ids = inner
      .filter(el => (el as any).type === Step && !!el.props.id)
      .map(el => el.props.id)
    if (firstRoot.props.order && firstRoot.props.order.length) {
      setInferred(firstRoot.props.order)
    } else if (ids.length) {
      setInferred(ids)
    }
  }, [firstRoot, setInferred])

  return <>{children}</>
}

// ---------------------------
// Example Usage
// ---------------------------
/*
export default function Example() {
  return (
    <FlowProvider
      initial="details"
      onDiscard={(r) => console.log("Discarded flow", r)}
      onComplete={() => console.log("Flow completed")}
    >
      <Root className="max-w-xl mx-auto p-4 space-y-4">
        <Step id="details">
          <DetailsStep />
        </Step>
        <Step id="confirm">
          <ConfirmStep />
        </Step>
        <Step id="done">
          <DoneStep />
        </Step>
      </Root>
    </FlowProvider>
  );
}

function DetailsStep() {
  const { next, discard } = useFlow();
  return (
    <div className="space-y-2 border rounded-2xl p-4">
      <h2 className="text-xl font-semibold">Details</h2>
      <p>Collect user details here…</p>
      <div className="flex gap-2">
        <button className="px-3 py-2 rounded-xl border" onClick={() => discard("user_cancelled")}>Discard</button>
        <button className="px-3 py-2 rounded-xl border" onClick={next}>Continue</button>
      </div>
    </div>
  );
}

function ConfirmStep() {
  const { prev, next, goTo, discard } = useFlow();
  return (
    <div className="space-y-2 border rounded-2xl p-4">
      <h2 className="text-xl font-semibold">Confirm</h2>
      <p>Show a summary and ask for confirmation…</p>
      <div className="flex gap-2 flex-wrap">
        <button className="px-3 py-2 rounded-xl border" onClick={prev}>Back</button>
        <button className="px-3 py-2 rounded-xl border" onClick={next}>Confirm</button>
        <button className="px-3 py-2 rounded-xl border" onClick={() => goTo("details")}>Edit details</button>
        <button className="px-3 py-2 rounded-xl border" onClick={() => discard("abandoned_at_confirm")}>Discard</button>
      </div>
    </div>
  );
}

function DoneStep() {
  const { discard } = useFlow();
  return (
    <div className="space-y-2 border rounded-2xl p-4">
      <h2 className="text-xl font-semibold">Done</h2>
      <p>All set! You can close the flow.</p>
      <button className="px-3 py-2 rounded-xl border" onClick={() => discard("after_complete")}>Close</button>
    </div>
  );
}
*/

// Namespaced export for convenience
export const Flow = { Provider: FlowProvider, Root, Step, useFlow }
